import React, { useEffect, useRef } from 'react';
import { ViewStyle, Pressable, StyleSheet, Animated, useWindowDimensions, KeyboardAvoidingView } from 'react-native';
import styled, { useTheme } from 'styled-components/native';
import { AppPressable } from '@/components/base/app.pressable';
import { AppTextView } from '@/components/base/app.text.view';
import { AppIconButton } from '@/components/base/app.icon.button';
import { Box, BoxProps } from '@/components/base/box';
import { GlobalAlert } from '@/components/compose/global.alert';
import { AppCardView } from '@/components/base/app.card.view';
import { useAppRouter } from '@/hooks/use.app.router';
import { useRoute } from '@react-navigation/native';
import { ScreenHeader } from '@/components/compose/screen.header';
import Svg, { Defs, RadialGradient, Rect, Stop } from 'react-native-svg';
import { AppKeyboardAvoidingView } from '@/components/compose/app.keyboard.avoding.view';

export interface Props extends BoxProps {
  fullScreen?: boolean;
  title?: string;
  backButton?: boolean;
  backButtonColor?: string;
  blur?: boolean;
  containerStyle?: ViewStyle;
  roundCorners?: boolean;
}

const AnimatedPressable = Animated.createAnimatedComponent(AppPressable);

function FadeBackdrop({ onPress }: { onPress: () => void }) {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const { width, height } = useWindowDimensions();

  // 1. Calculate absolute center and a large, uniform radius
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) * 0.9;

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 2000, // 2. Fixed the 2000ms delay to a snappy 300ms
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  return (
    <AnimatedPressable
      onPress={onPress}
      style={[
        StyleSheet.absoluteFill,
        {
          opacity: fadeAnim,
        },
      ]}
    >
      <Svg height="100%" width="100%" style={StyleSheet.absoluteFill}>
        <Defs>
          <RadialGradient id="vignette" cx={centerX} cy={centerY} r={radius} rx={radius} ry={radius} fx={centerX} fy={centerY} gradientUnits="userSpaceOnUse">
            {/* Extract the alpha from the rgba string into stopOpacity */}
            <Stop offset="10%" stopColor="rgb(15, 82, 83)" stopOpacity="0.5" />
            <Stop offset="100%" stopColor="rgb(0, 0, 0)" stopOpacity="0.7" />
          </RadialGradient>
        </Defs>
        <Rect x="0" y="0" width="100%" height="100%" fill="url(#vignette)" />
      </Svg>
    </AnimatedPressable>
  );
}

function BaseAppModal({ roundCorners = true, blur = false, backButton = true, backButtonColor, containerStyle, title, children, ...rest }: Props) {
  const route = useRoute<any>();
  const { goBack } = useAppRouter();

  const fullScreen = route.params?.fullScreen === true;
  if (!fullScreen) {
    blur = true;
  }

  const content = fullScreen ? (
    <FullScreen style={containerStyle} title={title} children={children} backButton={backButton} roundCorners={roundCorners} backButtonColor={backButtonColor} />
  ) : (
    <HalfScreen style={containerStyle} title={title} children={children} />
  );

  return (
    <AppKeyboardAvoidingView>
      {blur && <FadeBackdrop onPress={goBack} />}
      <ModalContainer fullScreen={fullScreen} removeClippedSubviews={!fullScreen} {...rest}>
        {content}
      </ModalContainer>
    </AppKeyboardAvoidingView>
  );
}

function FullScreen({ roundCorners, title, children, style, backButton, backButtonColor }: Props) {
  const { width } = useWindowDimensions(); // 🔥 Get current viewport width dynamically

  // 🔥 Separate animation nodes for clean interpolations
  const slideAnim = useRef(new Animated.Value(width)).current; // Start completely off-screen to the right
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // 🔥 Run slide and fade in parallel with an elegant cubic transition
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 350,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, [slideAnim, fadeAnim, width]);

  return (
    /* 🔥 Apply both the horizontal translation and opacity transforms natively */
    <Animated.View
      style={{
        flex: 1,
        width: '100%',
        opacity: fadeAnim,
        transform: [{ translateX: slideAnim }],
      }}
    >
      <FullScreenContainer style={style}>
        {title && <ScreenHeader backButton={backButton} text={title} backButtonColor={backButtonColor} />}
        <ChildrenContainer roundCorners={roundCorners}>{children}</ChildrenContainer>
        <GlobalAlert />
      </FullScreenContainer>
    </Animated.View>
  );
}

function HalfScreen({ title, children, style }: Props) {
  const theme = useTheme();
  const { goBack } = useAppRouter();
  const slideAnim = useRef(new Animated.Value(500)).current;

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 350,
      useNativeDriver: true,
    }).start();
  }, [slideAnim]);

  return (
    <HalfScreenBox>
      <Pressable style={StyleSheet.absoluteFill} onPress={goBack} />
      <Animated.View style={{ width: '100%', transform: [{ translateY: slideAnim }] }}>
        <Shadow>
          <HalfScreenContainer style={style}>
            <Header fullWidth flexDirection={'row-reverse'} justifyContent={'space-between'}>
              <AppTextView text={title} variant={'title'} bold textColor={'jade'} />
              <AppIconButton icon={'close'} color={theme.colors.icon.jade} size={'big'} onPress={goBack} />
            </Header>
            {children}
          </HalfScreenContainer>
        </Shadow>
      </Animated.View>
    </HalfScreenBox>
  );
}

const Header = styled(Box)`
  margin-bottom: 20px;
  width: 100%;
`;

const Shadow = styled(AppCardView)`
  border-radius: ${({ theme }) => theme.hp(3)}px;
  background-color: transparent;
  width: 100%;
`;

const HalfScreenBox = styled(Box)`
  flex: 1;
  width: 100%;
  justify-content: flex-end;
  background-color: transparent;
`;

const HalfScreenContainer = styled(Box)`
  background-color: white;
  border-top-left-radius: ${({ theme }) => theme.hp(3)}px;
  border-top-right-radius: ${({ theme }) => theme.hp(3)}px;
  flex-direction: column;
  padding: 5%;
  width: 100%;
`;

const FullScreenContainer = styled(Box)`
  background-color: ${({ theme }) => theme.colors.modal.title.background};
  flex: 1;
  width: 100%;
`;

const ChildrenContainer = styled(Box)<{ roundCorners: boolean }>`
  background-color: white;
  flex: 1;
  width: 100%;
  ${({ theme, roundCorners }) => roundCorners && `border-top-left-radius: ${theme.hp(3)}px;`}
  ${({ theme, roundCorners }) => roundCorners && `border-top-right-radius: ${theme.hp(3)}px;`}
`;

const ModalContainer = styled(Box)<Props>`
  flex: 1;
  width: 100%;
  background-color: transparent;
`;

export const AppModal = React.memo(BaseAppModal);
